/**
 * PackageProgressLine — finer-than-the-dot progress indicator shown next
 * to a package row while it's still mid-flight.
 *
 * Renders e.g. `Article ✓ · Hero ✓ · Pins 3/8` so admins can SEE that
 * the system is making progress rather than guessing whether the
 * "generating…" spinner is stuck. Only renders when the package is
 * actively generating (status / asset_generation_status / image_pipeline_status
 * say so); collapses to null otherwise.
 *
 * Reads progress signals from `asset_completion`:
 *   { article_done, hero_done, infographic_done, pin_done, pin_total,
 *     pipeline_phase: "article" | "hero" | "infographic" | "pins" | "complete" }
 * Falls back to compact dashes for older packages that predate the field.
 */
import { Check, Loader2 } from "lucide-react";

const PHASE_LABEL = {
    article: "Writing article (~4 min)",
    hero: "Rendering hero image",
    infographic: "Rendering infographic",
    pins: "Rendering Pinterest pins",
    complete: "Complete",
};

function isInProgress(pkg) {
    if (!pkg) return false;
    if (pkg.asset_generation_status === "in_progress") return true;
    if (pkg.image_pipeline_status === "generating") return true;
    if (pkg.hero_regeneration_status === "in_progress") return true;
    if (pkg.asset_completion?.hero_regen_active) return true;
    // Stub state right after POST /admin/content-packages — orchestrator
    // hasn't kicked off yet but the user is staring at the row.
    if (pkg.status === "generating" || pkg.asset_generation_status === "pending") {
        return true;
    }
    return false;
}

function Stage({ label, done, active }) {
    let icon;
    if (done) {
        icon = <Check className="h-3 w-3 text-emerald-600" aria-hidden />;
    } else if (active) {
        icon = <Loader2 className="h-3 w-3 animate-spin text-bl-muted" aria-hidden />;
    } else {
        icon = <span className="inline-block h-1.5 w-1.5 rounded-full bg-bl-muted/40" aria-hidden />;
    }
    return (
        <span
            className={
                "inline-flex items-center gap-1 " +
                (done
                    ? "text-emerald-700"
                    : active
                    ? "text-bl"
                    : "text-bl-muted/70")
            }
        >
            {icon}
            <span>{label}</span>
        </span>
    );
}

export default function PackageProgressLine({ pkg }) {
    if (!isInProgress(pkg)) return null;
    const c = pkg.asset_completion || {};
    const phase = c.pipeline_phase || "article";
    const pinTotal = c.pin_total ?? 8;
    const pinDone = c.pin_done ?? 0;
    const articleDone = !!c.article_done;
    const heroDone = !!c.hero_done;
    const infographicDone = !!c.infographic_done;

    return (
        <div
            className="mt-1 flex items-center gap-3 text-[11px] flex-wrap"
            data-testid={`package-progress-${pkg.package_id}`}
            aria-live="polite"
            aria-label={`Progress: ${PHASE_LABEL[phase] || phase}`}
        >
            <Stage
                label="Article"
                done={articleDone}
                active={phase === "article"}
            />
            <span className="text-bl-muted/40" aria-hidden>·</span>
            <Stage
                label="Hero"
                done={heroDone}
                active={phase === "hero"}
            />
            <span className="text-bl-muted/40" aria-hidden>·</span>
            <Stage
                label="Infographic"
                done={infographicDone}
                active={phase === "infographic"}
            />
            <span className="text-bl-muted/40" aria-hidden>·</span>
            <span
                className={
                    "inline-flex items-center gap-1 tnum " +
                    (pinDone >= pinTotal
                        ? "text-emerald-700"
                        : phase === "pins"
                        ? "text-bl"
                        : "text-bl-muted/70")
                }
                data-testid={`package-progress-pins-${pkg.package_id}`}
            >
                {pinDone >= pinTotal ? (
                    <Check className="h-3 w-3 text-emerald-600" aria-hidden />
                ) : phase === "pins" ? (
                    <Loader2
                        className="h-3 w-3 animate-spin text-bl-muted"
                        aria-hidden
                    />
                ) : (
                    <span
                        className="inline-block h-1.5 w-1.5 rounded-full bg-bl-muted/40"
                        aria-hidden
                    />
                )}
                Pins {pinDone}/{pinTotal}
            </span>
            <span
                className="text-bl-muted/60 italic"
                data-testid={`package-progress-eta-${pkg.package_id}`}
            >
                ~6 min total · auto-refreshes
            </span>
        </div>
    );
}
